import React from "react";

/**
 * Minimal Markdown renderer — h2/h3/lists/bold/italic/paragraphs only.
 * Same pattern used by LearnArticlePage. Reused by /shop product pages
 * so admins can paste editorial-quality markdown for descriptions,
 * ingredients and usage without a heavy dependency.
 *
 * Outputs React nodes (no `dangerouslySetInnerHTML`) — XSS-safe.
 */
export function renderMarkdown(md, options = {}) {
  const { textClass = "leading-relaxed text-emerald-950/90", listClass = "list-disc pl-5 my-3 space-y-1.5 text-emerald-950/90", h2Class = "font-display text-xl sm:text-2xl text-emerald-950 mt-6 mb-2.5 leading-snug", h3Class = "font-display text-lg text-emerald-950 mt-5 mb-2" } = options;
  const lines = (md || "").split(/\r?\n/);
  const blocks = [];
  let currentList = null;
  let currentPara = [];

  const flushPara = () => {
    if (currentPara.length) {
      blocks.push({ type: "p", content: currentPara.join(" ") });
      currentPara = [];
    }
  };
  const flushList = () => {
    if (currentList) {
      blocks.push({ type: "ul", items: currentList });
      currentList = null;
    }
  };

  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); return; }
    if (line.startsWith("## ")) { flushPara(); flushList(); blocks.push({ type: "h2", content: line.slice(3).trim() }); return; }
    if (line.startsWith("### ")) { flushPara(); flushList(); blocks.push({ type: "h3", content: line.slice(4).trim() }); return; }
    if (line.startsWith("- ") || line.startsWith("• ")) { flushPara(); currentList = currentList || []; currentList.push(line.replace(/^[-•]\s*/, "").trim()); return; }
    if (/^\d+\.\s/.test(line)) { flushPara(); currentList = currentList || []; currentList.push(line.replace(/^\d+\.\s/, "").trim()); return; }
    flushList();
    currentPara.push(line);
  });
  flushPara();
  flushList();

  const renderInline = (text) => {
    const parts = [];
    let key = 0;
    const splitter = /(\*\*[^*]+\*\*|_[^_]+_)/g;
    text.split(splitter).forEach((seg) => {
      if (!seg) return;
      if (seg.startsWith("**") && seg.endsWith("**")) {
        parts.push(<strong key={`b${key++}`}>{seg.slice(2, -2)}</strong>);
      } else if (seg.startsWith("_") && seg.endsWith("_")) {
        parts.push(<em key={`i${key++}`}>{seg.slice(1, -1)}</em>);
      } else {
        parts.push(seg);
      }
    });
    return parts;
  };

  return blocks.map((b, idx) => {
    if (b.type === "h2") return <h2 key={idx} className={h2Class}>{renderInline(b.content)}</h2>;
    if (b.type === "h3") return <h3 key={idx} className={h3Class}>{renderInline(b.content)}</h3>;
    if (b.type === "ul") return (
      <ul key={idx} className={listClass}>
        {b.items.map((it, i) => <li key={i} className="leading-relaxed">{renderInline(it)}</li>)}
      </ul>
    );
    return <p key={idx} className={`my-3 ${textClass}`}>{renderInline(b.content)}</p>;
  });
}
